<template>
    <el-container>
        <el-header>
            <span>{{$t('report.headerTitle')}}</span>
        </el-header>
        <el-main class="report-main">
            <el-alert v-if="loadError !== ''" :title="loadError" type="error" :closable="false" show-icon></el-alert>
            <template v-else-if="contextLoaded">
            <div class="report-tabs">
                <div class="report-tab-list">
                    <button :class="['report-tab', activeReportTab === 'dashboard' ? 'active' : '']" type="button" @click="changeReportTab('dashboard')">{{$t('report.tabDashboard')}}</button>
                    <button :class="['report-tab', activeReportTab === 'reportList' ? 'active' : '']" type="button" @click="changeReportTab('reportList')">{{$t('report.tabReportList')}}</button>
                </div>
                <el-select v-if="activeReportTab === 'dashboard'" class="year-select" v-model="selectedYear" @change="queryReportData">
                    <el-option
                            v-for="yearOption in yearOptions"
                            :key="yearOption.key"
                            :label="yearOption.label"
                            :value="yearOption.value"></el-option>
                </el-select>
                <button v-if="activeReportTab === 'reportList'" class="export-button" type="button"
                        :disabled="!reportListLoaded" @click="exportReportListCsv">
                    {{$t('report.export')}}
                    <i class="el-icon-arrow-down"></i>
                </button>
            </div>

            <template v-if="activeReportTab === 'dashboard'">
                <section class="report-section report-overview-section" v-loading="loading">
                    <div class="report-overview-grid">
                        <div class="report-panel">
                            <h2>{{$t('report.annualSalesByClient')}}</h2>
                            <el-alert v-if="clientSalesLoadError !== ''" :title="clientSalesLoadError" type="error" :closable="false" show-icon></el-alert>
                            <div v-else-if="clientSalesList.length === 0" class="empty-report">{{$t('report.noDataForDateRange')}}</div>
                            <div v-else class="client-chart-row">
                                <div class="donut-chart-wrap">
                                    <svg class="donut-chart" viewBox="0 0 200 200">
                                        <path
                                                v-for="client in clientPieSliceList"
                                                :key="'pie-' + client.innerUUID"
                                                class="donut-slice"
                                                :fill="client.color"
                                                :d="client.path"
                                                @mouseenter="showClientPieTooltip(client, $event)"
                                                @mousemove="moveClientPieTooltip($event)"
                                                @mouseleave="hideClientPieTooltip"
                                                @click="goInvoiceListByClient(client)"></path>
                                    </svg>
                                    <div
                                            v-if="clientPieTooltip.visible"
                                            class="client-pie-tooltip"
                                            :style="{left: clientPieTooltip.left + 'px', top: clientPieTooltip.top + 'px'}">
                                        {{clientPieTooltip.content}}
                                    </div>
                                </div>
                                <div class="client-sales-list">
                                    <div class="client-sales-row" v-for="client in clientSalesList" :key="client.innerUUID" @click="goInvoiceListByClient(client)">
                                        <span class="client-color" :style="{background: client.color}"></span>
                                        <span class="client-name">{{client.name}} - {{client.percent}}%</span>
                                        <strong>{{formatMoney(client.totalMoney)}}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="report-panel">
                            <h2>{{$t('report.totalOutstandingBalance')}}</h2>
                            <el-alert v-if="outstandingLoadError !== ''" :title="outstandingLoadError" type="error" :closable="false" show-icon></el-alert>
                            <template v-else>
                                <div class="outstanding-total">{{formatMoney(totalOutstandingBalance)}}</div>
                                <div class="outstanding-list">
                                    <div class="outstanding-row" v-for="client in displayOutstandingBalanceList" :key="client.innerUUID" @click="goInvoiceListByClient(client)">
                                        <strong>{{client.name}}</strong>
                                        <strong>{{formatMoney(client.totalOutstandingBalance)}}</strong>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </section>

                <section class="report-section sales-section" v-loading="loading">
                    <div class="report-section-header">
                        <div>
                            <h2>{{saleReportTitle}}</h2>
                            <div v-if="reportSaleLoadError === ''" class="invoice-count">{{reportSummary.totalInvoiceCount}} {{$t('report.invoicesCountSuffix')}}</div>
                            <div v-if="reportSaleLoadError === ''" class="total-sales">{{formatMoney(reportSummary.totalMoney)}}</div>
                        </div>
                    </div>

                    <el-alert v-if="reportSaleLoadError !== ''" :title="reportSaleLoadError" type="error" :closable="false" show-icon></el-alert>
                    <template v-else>
                        <div class="report-mode-tabs">
                            <button :class="['mode-button', dateIncrement === 'month' ? 'active' : '']" type="button" @click="changeDateIncrement('month')">{{$t('report.byMonth')}}</button>
                            <button :class="['mode-button', dateIncrement === 'quarter' ? 'active' : '']" type="button" @click="changeDateIncrement('quarter')">{{$t('report.byQuarter')}}</button>
                        </div>

                    <div v-if="displayPeriodList.length > 0" class="bar-chart-wrap">
                        <div v-if="selectedYear === null && displayPeriodList.length > 10" class="bar-chart-actions">
                            <el-button size="mini" icon="el-icon-arrow-left" circle @click="scrollSaleChart(-1)"></el-button>
                            <el-button size="mini" icon="el-icon-arrow-right" circle @click="scrollSaleChart(1)"></el-button>
                        </div>
                        <div ref="saleChartScroll" class="bar-chart-scroll" :style="{width: chartViewportWidth}" @wheel="handleSaleChartWheel">
                            <div class="bar-chart" :style="{gridTemplateColumns: chartGridTemplateColumns, width: chartWidth}">
                                <div
                                        v-for="period in displayPeriodList"
                                        :key="period.periodKey"
                                        class="bar-column">
                                    <el-tooltip effect="dark" :content="getSaleTooltip(period)" placement="top">
                                        <div class="bar-track">
                                            <div class="bar-value" :style="{height: getBarHeight(period.totalMoney)}"></div>
                                        </div>
                                    </el-tooltip>
                                    <div class="bar-label">{{period.periodName}}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="empty-report">{{$t('report.noSalesFound')}}</div>

                        <div class="period-list">
                            <div class="period-row" v-for="period in displayPeriodTableList" :key="'row-' + period.periodKey" @click="goInvoiceListBySalePeriod(period)">
                                <span>{{period.title}} - {{period.invoiceCount}} {{$t('report.invoicesCountSuffix')}}</span>
                                <strong>{{formatMoney(period.totalMoney)}}</strong>
                            </div>
                        </div>
                    </template>
                </section>
            </template>

            <template v-else>
                <section class="report-list-section" v-loading="loading">
                    <div class="report-list-filters">
                        <div class="report-list-filter">
                            <span>{{$t('report.filterReportTypes')}}</span>
                            <el-select class="report-list-select" v-model="reportListType" @change="changeReportListType">
                                <el-option
                                        v-for="reportTypeOption in reportTypeOptions"
                                        :key="reportTypeOption.value"
                                        :label="reportTypeOption.label"
                                        :value="reportTypeOption.value"></el-option>
                            </el-select>
                        </div>
                        <div v-if="showReportListTimePeriodFilter" class="report-list-filter">
                            <span>{{$t('report.filterTimePeriod')}}</span>
                            <el-select class="report-list-select" v-model="reportListPeriodSelectionType" @change="changeReportListPeriodSelectionType">
                                <el-option
                                        v-for="periodTypeOption in reportListPeriodTypeOptions"
                                        :key="'report-list-period-' + periodTypeOption.value"
                                        :label="periodTypeOption.label"
                                        :value="periodTypeOption.value"></el-option>
                            </el-select>
                        </div>
                        <div v-if="showReportListYearFilter" class="report-list-filter">
                            <span>{{$t('report.filterTaxYear')}}</span>
                            <el-select class="report-list-select" v-model="selectedReportListYear" @change="queryReportListData">
                                <el-option
                                        v-for="yearOption in reportListYearOptions"
                                        :key="'report-list-year-' + yearOption.key"
                                        :label="yearOption.label"
                                        :value="yearOption.value"></el-option>
                            </el-select>
                        </div>
                        <div v-if="showReportListQuarterFilter" class="report-list-filter report-list-filter-compact">
                            <span>{{$t('report.filterQuarter')}}</span>
                            <el-select class="report-list-select" v-model="selectedReportListPeriodIndex" @change="queryReportListData">
                                <el-option
                                        v-for="quarterOption in reportListQuarterOptions"
                                        :key="'report-list-quarter-' + quarterOption.value"
                                        :label="quarterOption.label"
                                        :value="quarterOption.value"></el-option>
                            </el-select>
                        </div>
                        <div v-if="showReportListMonthFilter" class="report-list-filter">
                            <span>{{$t('report.filterMonth')}}</span>
                            <el-select class="report-list-select" v-model="selectedReportListPeriodIndex" @change="queryReportListData">
                                <el-option
                                        v-for="monthOption in reportListMonthOptions"
                                        :key="'report-list-month-' + monthOption.value"
                                        :label="monthOption.label"
                                        :value="monthOption.value"></el-option>
                            </el-select>
                        </div>
                        <div v-if="showReportListCustomDateFilter" class="report-list-filter report-list-date-filter">
                            <span>{{$t('report.filterStartDate')}}</span>
                            <el-date-picker
                                    class="report-list-select"
                                    v-model="reportListCustomStartDate"
                                    type="date"
                                    value-format="yyyy-MM-dd"
                                    format="yyyy-MM-dd"
                                    :clearable="false"
                                    :picker-options="reportListCustomStartPickerOptions"
                                    @change="changeReportListCustomDate"></el-date-picker>
                        </div>
                        <div v-if="showReportListCustomDateFilter" class="report-list-filter report-list-date-filter">
                            <span>{{$t('report.filterEndDate')}}</span>
                            <el-date-picker
                                    class="report-list-select"
                                    v-model="reportListCustomEndDate"
                                    type="date"
                                    value-format="yyyy-MM-dd"
                                    format="yyyy-MM-dd"
                                    :clearable="false"
                                    :picker-options="reportListCustomEndPickerOptions"
                                    @change="changeReportListCustomDate"></el-date-picker>
                        </div>
                        <div v-if="showReportListGroupTypeFilter" class="report-list-filter">
                            <span>{{$t('report.filterView')}}</span>
                            <el-select class="report-list-select" v-model="reportListDateIncrement" @change="queryReportListData">
                                <el-option :label="$t('report.optionMonthly')" value="month"></el-option>
                                <el-option :label="$t('report.optionQuarterly')" value="quarter"></el-option>
                            </el-select>
                        </div>
                    </div>

                    <el-alert v-if="reportListLoadError !== ''" :title="reportListLoadError" type="error" :closable="false" show-icon></el-alert>
                    <div v-if="reportListLoaded" class="report-list-summary">
                        <div v-if="isReportListJournal" class="report-list-year-title">{{reportListJournalTitle}}</div>
                        <template v-else-if="reportListType === 'CUSTOMER_AGING'">
                            <div class="report-list-year-title">{{$t('report.customerAgingTitle')}}</div>
                        </template>
                        <template v-else-if="isStructuredReportList">
                            <div class="report-list-year-title">{{structuredReportListTitle}}</div>
                        </template>
                        <template v-else>
                            <div class="invoice-count">{{reportListSummary.summaryCount}} {{reportListSummary.summaryCountName}}</div>
                            <div class="total-sales">{{formatReportListMoney(reportListSummary.summaryTotalMoney)}}</div>
                        </template>
                    </div>

                    <div v-if="reportListChartType === 'BAR' && reportListChartList.length > 0" class="bar-chart-wrap report-list-chart-wrap">
                        <div v-if="reportListTimePeriodType === 'ALL_TIME' && reportListChartList.length > 10" class="bar-chart-actions">
                            <el-button size="mini" icon="el-icon-arrow-left" circle @click="scrollReportListChart(-1)"></el-button>
                            <el-button size="mini" icon="el-icon-arrow-right" circle @click="scrollReportListChart(1)"></el-button>
                        </div>
                        <div ref="reportListSaleChartScroll" class="bar-chart-scroll" :style="{width: chartViewportWidth}" @wheel="handleReportListChartWheel">
                            <div class="bar-chart" :style="{gridTemplateColumns: reportListChartGridTemplateColumns, width: reportListChartWidth}">
                                <div
                                        v-for="period in reportListChartList"
                                        :key="'report-list-chart-' + period.periodName"
                                        class="bar-column">
                                    <el-tooltip effect="dark" :content="getReportListSaleTooltip(period)" placement="top">
                                        <div class="bar-track">
                                            <div class="bar-value" :style="{height: getReportListBarHeight(period.totalMoney)}"></div>
                                        </div>
                                    </el-tooltip>
                                    <div class="bar-label">{{getReportListChartLabel(period)}}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="reportListChartType === 'DONUT' && reportListChartList.length > 0" class="report-list-donut-row">
                        <div class="report-list-donut-wrap">
                            <svg class="report-list-donut" viewBox="0 0 200 200">
                                <path
                                        v-for="chartItem in reportListDonutSliceList"
                                        :key="'report-list-donut-' + chartItem.index"
                                        class="donut-slice"
                                        :fill="chartItem.color"
                                        :d="chartItem.path"
                                        @click="goInvoiceListByReportClient(chartItem)"></path>
                            </svg>
                        </div>
                        <div class="report-list-donut-legend">
                            <div class="report-list-donut-row-item" v-for="chartItem in reportListChartLegendList" :key="'report-list-legend-' + chartItem.index" @click="goInvoiceListByReportClient(chartItem)">
                                <span class="client-color" :style="{background: chartItem.color}"></span>
                                <strong>{{chartItem.name}} - {{chartItem.percent}}%</strong>
                                <strong>{{formatReportListMoney(chartItem.totalMoney)}}</strong>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="reportListLoaded && showReportListEmptyChart" class="empty-report">{{$t('report.noDataForDateRange')}}</div>
                </section>

                <section v-if="reportListLoaded" class="report-detail-section" v-loading="loading">
                    <div v-if="showReportListSearch" class="report-list-table-search">
                        <el-input
                                v-model="reportListSearchContent"
                                prefix-icon="el-icon-search"
                                clearable
                                :placeholder="reportListSearchPlaceholder"></el-input>
                    </div>
                    <div ref="reportTableScroll" class="report-detail-table-wrap"
                         @scroll="syncReportStickyScrollbarFromTable">
                        <table :class="['report-detail-table', {
                            'customer-aging-table': reportListType === 'CUSTOMER_AGING',
                            'sales-by-client-table': reportListType === 'SALES_BY_CLIENT',
                            'sales-by-item-table': reportListType === 'SALES_BY_ITEM',
                            'sales-by-date-year-table': reportListType === 'SALES_BY_DATE' && reportListGroupType === 'YEAR',
                            'taxes-table': reportListType === 'TAXES',
                            'taxes-year-table': reportListType === 'TAXES' && reportListGroupType === 'YEAR',
                            'sales-journal-table': reportListType === 'SALES_JOURNAL',
                            'payments-journal-table': reportListType === 'PAYMENTS_JOURNAL',
                            'report-compact-table': reportListType === 'PAYMENTS_BY_DATE'
                                || reportListType === 'PAYMENTS_BY_CLIENT'
                                || reportListType === 'PAYMENTS_BY_TYPE'
                                || reportListType === 'PROFIT_LOSS',
                            'profit-loss-table': reportListType === 'PROFIT_LOSS'
                        }]">
                            <colgroup v-if="reportListType === 'TAXES' && reportListGroupType === 'YEAR'">
                                <col class="taxes-year-col">
                                <col class="taxes-value-col">
                                <col class="taxes-value-col">
                                <col class="taxes-value-col">
                                <col class="taxes-value-col">
                                <col class="taxes-value-col">
                            </colgroup>
                            <colgroup v-if="reportListType === 'CUSTOMER_AGING'">
                                <col class="customer-aging-client-col">
                                <col>
                                <col>
                                <col>
                                <col>
                                <col>
                                <col>
                            </colgroup>
                            <thead>
                            <tr>
                                <th v-for="column in reportListColumnList" :key="'report-list-head-' + column.key" @click="changeReportListSort(column)">
                                    <span class="list-sort-header" :class="{'is-active': reportListSortKey === column.key}">
                                        {{column.label}}
                                        <svg v-if="reportListSortKey === column.key" class="list-sort-icon is-active" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                            <path v-if="reportListSortOrder === 1" d="M8 6L12 2L16 6"/>
                                            <path v-else d="M8 18L12 22L16 18"/>
                                            <path d="M12 2V22"/>
                                        </svg>
                                    </span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr
                                    v-for="(row, rowIndex) in sortedReportListDetailList"
                                    :key="'report-list-detail-' + rowIndex"
                                    :class="{'report-list-clickable-row': reportListType === 'SALES_BY_CLIENT'}"
                                    @click="goInvoiceListByReportClientRow(row)">
                                <td v-for="column in reportListColumnList" :key="'report-list-cell-' + rowIndex + '-' + column.key">
                                    {{formatReportListCell(row, column)}}
                                </td>
                            </tr>
                            <tr v-if="showReportListTotalRow" class="report-detail-total-row">
                                <td v-for="column in reportListColumnList" :key="'report-list-total-' + column.key">
                                    {{formatReportListCell(reportListTotalRow, column)}}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-show="reportStickyScrollbarVisible"
                         ref="reportStickyScrollbar"
                         class="report-sticky-scrollbar"
                         tabindex="0"
                         role="scrollbar"
                         aria-orientation="horizontal"
                         aria-label="Report table horizontal scrollbar"
                         :aria-valuemin="0"
                         :aria-valuemax="Math.max(0, reportStickyScrollbarContentWidth - reportStickyScrollbarViewportWidth)"
                         :aria-valuenow="reportStickyScrollbarScrollLeft"
                         :style="{
                             left: reportStickyScrollbarLeft + 'px',
                             width: reportStickyScrollbarViewportWidth + 'px'
                         }"
                         @keydown.left.prevent="scrollReportTable(-1)"
                         @keydown.right.prevent="scrollReportTable(1)">
                        <div ref="reportStickyScrollbarTrack"
                             class="report-sticky-scrollbar-track"
                             @mousedown="moveReportTableFromStickyTrack">
                            <div class="report-sticky-scrollbar-thumb"
                                 :style="{
                                     width: reportStickyScrollbarThumbWidth + 'px',
                                     transform: 'translateX(' + reportStickyScrollbarThumbLeft + 'px)'
                                 }"
                                 @mousedown.stop.prevent="startReportStickyScrollbarDrag"></div>
                        </div>
                    </div>
                </section>
            </template>
            </template>

        </el-main>
    </el-container>
</template>

<script>
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {syncCompanySelection} from '@/utils/company'
    import {getBrowserStartOfTodayEpochSecond, getBrowserTimeZone} from '@/utils/browserTimeZone'
    import {readListQueryState, writeListQueryState} from '@/utils/listQueryState'
    import {storedTaxRateToPercentage} from '@/utils/taxInputValidation'

    export default {
        data() {
            return {
                activeReportTab: "dashboard",
                loading: false,
                selectedYear: null,
                dateIncrement: "month",
                selectedReportListYear: null,
                reportListPeriodSelectionType: "ALL_TIME",
                selectedReportListPeriodIndex: 1,
                reportListStartYear: null,
                reportListEndYear: null,
                reportListCustomStartDate: "",
                reportListCustomEndDate: "",
                reportListDateIncrement: "month",
                reportListType: "SALES_BY_DATE",
                userSetting: null,
                companyUUID: '',
                contextLoaded: false,
                loadError: '',
                reportSaleLoadError: '',
                clientSalesLoadError: '',
                outstandingLoadError: '',
                reportListLoadError: '',
                reportSummary: {
                    totalInvoiceCount: 0,
                    totalMoney: 0
                },
                reportListSummary: {
                    reportType: "SALES_BY_DATE",
                    currencyName: "",
                    summaryCount: 0,
                    summaryCountName: "",
                    summaryTotalMoney: 0
                },
                reportBarList: [],
                reportListChartList: [],
                reportListDetailList: [],
                reportListTotalRow: {},
                reportListChartType: "BAR",
                reportListSortKey: "",
                reportListSortOrder: "",
                reportListSearchContent: "",
                reportListLoaded: false,
                clientSalesList: [],
                outstandingBalanceList: [],
                loadingCount: 0,
                reportSaleRequestSequence: 0,
                clientSalesRequestSequence: 0,
                outstandingRequestSequence: 0,
                reportListRequestSequence: 0,
                reportStickyScrollbarVisible: false,
                reportStickyScrollbarLeft: 0,
                reportStickyScrollbarViewportWidth: 0,
                reportStickyScrollbarContentWidth: 0,
                reportStickyScrollbarScrollLeft: 0,
                reportStickyScrollbarThumbWidth: 0,
                reportStickyScrollbarThumbLeft: 0,
                reportStickyScrollbarDragStartX: 0,
                reportStickyScrollbarDragStartLeft: 0,
                clientPieTooltip: {
                    visible: false,
                    content: "",
                    left: 0,
                    top: 0
                },
                clientColorList: ["#217BD3", "#31A66A", "#F2A541", "#E05252", "#6B5DD3", "#00A7A7", "#7A5230", "#8C8C8C"],
                reportListColorList: ["#8BE6CB", "#8FC5FF", "#FFF1A6", "#FFB56B", "#EB6A6A", "#8C7BFF", "#65D2D2", "#9B9B9B"]
            }
        },
        computed: {
            reportTypeOptions() {
                return [
                    {label: this.$t('report.typeSalesByDate'), value: "SALES_BY_DATE"},
                    {label: this.$t('report.typeSalesByClient'), value: "SALES_BY_CLIENT"},
                    {label: this.$t('report.typeSalesByItem'), value: "SALES_BY_ITEM"},
                    {label: this.$t('report.typeSalesJournal'), value: "SALES_JOURNAL"},
                    {label: this.$t('report.typePaymentsByDate'), value: "PAYMENTS_BY_DATE"},
                    {label: this.$t('report.typePaymentsByClient'), value: "PAYMENTS_BY_CLIENT"},
                    {label: this.$t('report.typePaymentsByType'), value: "PAYMENTS_BY_TYPE"},
                    {label: this.$t('report.typePaymentsJournal'), value: "PAYMENTS_JOURNAL"},
                    {label: this.$t('report.typeExpenseByCategory'), value: "EXPENSE_BY_CATEGORY"},
                    {label: this.$t('report.typeExpenseJournal'), value: "EXPENSE_JOURNAL"},
                    {label: this.$t('report.typeProfitLoss'), value: "PROFIT_LOSS"},
                    {label: this.$t('report.typeTaxes'), value: "TAXES"},
                    {label: this.$t('report.typeCustomerAging'), value: "CUSTOMER_AGING"}
                ];
            },
            yearOptions() {
                let yearList = [{
                    key: "all",
                    label: this.$t('report.allYears'),
                    value: null
                }];
                let currentYear = this.getCurrentFiscalYearEnd();
                for (let i = currentYear + 1; i >= 2018; i--) {
                    yearList.push({
                        key: String(i),
                        label: String(i),
                        value: i
                    });
                }
                return yearList;
            },
            reportListYearOptions() {
                if (!Number.isInteger(this.reportListStartYear)
                    || !Number.isInteger(this.reportListEndYear)
                    || this.reportListStartYear > this.reportListEndYear) {
                    return [];
                }
                let yearList = [];
                for (let year = this.reportListEndYear; year >= this.reportListStartYear; year--) {
                    yearList.push({
                        key: String(year),
                        label: String(year),
                        value: year
                    });
                }
                return yearList;
            },
            reportListPeriodTypeOptions() {
                return [
                    {label: this.$t('report.periodMonth'), value: "MONTH"},
                    {label: this.$t('report.periodQuarter'), value: "QUARTER"},
                    {label: this.$t('report.periodYear'), value: "TAX_YEAR"},
                    {label: this.$t('report.periodAllTaxYears'), value: "ALL_TIME"},
                    {label: this.$t('report.periodCustom'), value: "CUSTOM"}
                ];
            },
            reportListQuarterOptions() {
                return [1, 2, 3, 4].map(quarter => ({
                    label: "Q" + quarter,
                    value: quarter
                }));
            },
            reportListMonthOptions() {
                if (this.selectedReportListYear === null) {
                    return [];
                }
                let taxYearRange = this.getFiscalYearDateRange(this.selectedReportListYear);
                let options = [];
                for (let monthIndex = 1; monthIndex <= 12; monthIndex++) {
                    let monthDate = this.addMonthsClamped(taxYearRange.startDate, monthIndex - 1);
                    options.push({
                        label: this.formatReportMonth(monthDate) + " " + monthDate.getFullYear(),
                        value: monthIndex
                    });
                }
                return options;
            },
            showReportListTimePeriodFilter() {
                return this.reportListType !== "CUSTOMER_AGING";
            },
            reportListTimePeriodType() {
                return this.reportListPeriodSelectionType;
            },
            showReportListYearFilter() {
                return this.showReportListTimePeriodFilter
                    && (this.reportListTimePeriodType === "TAX_YEAR"
                        || this.reportListTimePeriodType === "QUARTER"
                        || this.reportListTimePeriodType === "MONTH");
            },
            showReportListQuarterFilter() {
                return this.showReportListTimePeriodFilter && this.reportListTimePeriodType === "QUARTER";
            },
            showReportListMonthFilter() {
                return this.showReportListTimePeriodFilter && this.reportListTimePeriodType === "MONTH";
            },
            showReportListCustomDateFilter() {
                return this.showReportListTimePeriodFilter && this.reportListTimePeriodType === "CUSTOM";
            },
            reportListCustomStartPickerOptions() {
                return {
                    disabledDate: date => date.getTime() > this.parseReportDateInput(this.reportListCustomEndDate).getTime()
                };
            },
            reportListCustomEndPickerOptions() {
                return {
                    disabledDate: date => date.getTime() < this.parseReportDateInput(this.reportListCustomStartDate).getTime()
                };
            },
            showReportListGroupTypeFilter() {
                return this.reportListRequiresGroupType && this.reportListTimePeriodType === "TAX_YEAR";
            },
            reportListRequiresGroupType() {
                return this.reportListType === "SALES_BY_DATE"
                    || this.reportListType === "PAYMENTS_BY_DATE"
                    || this.reportListType === "TAXES";
            },
            isStructuredReportList() {
                return this.reportListType === "PROFIT_LOSS" || this.reportListType === "TAXES";
            },
            structuredReportListTitle() {
                if (this.reportListType === "PROFIT_LOSS") {
                    return this.$t('report.profitLossTitle');
                }
                if (this.reportListType === "TAXES") {
                    return this.$t('report.taxesTitle');
                }
                return "";
            },
            showReportListTotalRow() {
                return this.reportListType !== "PROFIT_LOSS";
            },
            isReportListJournal() {
                return this.reportListType === "SALES_JOURNAL"
                    || this.reportListType === "PAYMENTS_JOURNAL"
                    || this.reportListType === "EXPENSE_JOURNAL";
            },
            showReportListSearch() {
                return this.reportListType === "SALES_BY_CLIENT"
                    || this.reportListType === "PAYMENTS_BY_CLIENT"
                    || this.reportListType === "SALES_BY_ITEM"
                    || this.reportListType === "SALES_JOURNAL"
                    || this.reportListType === "PAYMENTS_JOURNAL";
            },
            reportListSearchPlaceholder() {
                if (this.reportListType === "SALES_BY_CLIENT" || this.reportListType === "PAYMENTS_BY_CLIENT") {
                    return this.$t('report.searchClientName');
                }
                if (this.reportListType === "SALES_BY_ITEM") {
                    return this.$t('report.searchItemName');
                }
                if (this.reportListType === "SALES_JOURNAL") {
                    return this.$t('report.searchSalesJournal');
                }
                if (this.reportListType === "PAYMENTS_JOURNAL") {
                    return this.$t('report.searchPaymentsJournal');
                }
                return "";
            },
            reportListJournalTitle() {
                return this.reportListSelectedPeriodTitle;
            },
            reportListSelectedPeriodTitle() {
                if (this.reportListTimePeriodType === "ALL_TIME") {
                    return this.$t('report.periodAllTaxYears');
                }
                if (this.reportListTimePeriodType === "TAX_YEAR") {
                    return this.$t('report.taxYearLabel', {year: this.selectedReportListYear});
                }
                if (this.reportListTimePeriodType === "QUARTER") {
                    return this.$t('report.quarterLabel', {
                        quarter: this.selectedReportListPeriodIndex,
                        year: this.selectedReportListYear
                    });
                }
                if (this.reportListTimePeriodType === "MONTH") {
                    return this.reportListMonthOptions[this.selectedReportListPeriodIndex - 1].label;
                }
                if (this.reportListTimePeriodType === "CUSTOM") {
                    return this.formatReportDate(this.parseReportDateInput(this.reportListCustomStartDate))
                        + " - " + this.formatReportDate(this.parseReportDateInput(this.reportListCustomEndDate));
                }
                return "";
            },
            saleReportTitle() {
                if (this.selectedYear === null) {
                    return this.$t('report.allSales');
                }
                return this.$t('report.taxYearSales', {year: this.selectedYear});
            },
            groupType() {
                if (this.dateIncrement === "month") {
                    return "MONTH";
                }
                if (this.dateIncrement === "quarter") {
                    return "QUARTER";
                }
                return "MONTH";
            },
            reportListGroupType() {
                if (this.reportListTimePeriodType === "ALL_TIME") {
                    return "YEAR";
                }
                if (this.reportListTimePeriodType === "TAX_YEAR") {
                    if (this.reportListDateIncrement === "month") {
                        return "MONTH";
                    }
                    if (this.reportListDateIncrement === "quarter") {
                        return "QUARTER";
                    }
                    return "MONTH";
                }
                if (this.reportListTimePeriodType === "QUARTER") {
                    return "MONTH";
                }
                if (this.reportListTimePeriodType === "MONTH") {
                    return "DAY";
                }
                if (this.reportListTimePeriodType === "CUSTOM") {
                    return "RANGE";
                }
                return "RANGE";
            },
            reportListFirstColumnLabel() {
                if (this.reportListGroupType === "YEAR") {
                    return this.$t('report.firstColumnYear');
                }
                if (this.reportListGroupType === "MONTH") {
                    return this.$t('report.firstColumnMonth');
                }
                if (this.reportListGroupType === "QUARTER") {
                    return this.$t('report.firstColumnQuarter');
                }
                if (this.reportListGroupType === "DAY") {
                    return this.$t('report.firstColumnDay');
                }
                if (this.reportListGroupType === "RANGE") {
                    return this.$t('report.firstColumnDate');
                }
                return this.$t('report.firstColumnDate');
            },
            reportListColumnList() {
                if (this.reportListType === "SALES_BY_DATE") {
                    return [
                        {key: "displayName", label: this.reportListFirstColumnLabel, type: "text"},
                        {key: "subtotalMoney", label: this.$t('report.columnSubTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "amountPaid", label: this.$t('report.columnAmountPaid'), type: "money"},
                        {key: "amountOwed", label: this.$t('report.columnAmountOwed'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "SALES_BY_CLIENT") {
                    return [
                        {key: "clientName", label: this.$t('report.columnClient'), type: "text"},
                        {key: "subtotalMoney", label: this.$t('report.columnSubTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "amountPaid", label: this.$t('report.columnAmountPaid'), type: "money"},
                        {key: "amountOwed", label: this.$t('report.columnAmountOwed'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "SALES_BY_ITEM") {
                    return [
                        {key: "itemName", label: this.$t('report.columnItem'), type: "text"},
                        {key: "unitType", label: this.$t('report.columnUnitType'), type: "text"},
                        {key: "quantity", label: this.$t('report.columnQuantity'), type: "number"},
                        {key: "rateAvg", label: this.$t('report.columnRateAvg'), type: "money"},
                        {key: "subtotalMoney", label: this.$t('report.columnSubTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "SALES_JOURNAL") {
                    return [
                        {key: "invoiceDate", label: this.$t('report.columnInvoiceDate'), type: "date"},
                        {key: "invoiceNo", label: this.$t('report.columnInvoiceNo'), type: "text"},
                        {key: "clientName", label: this.$t('report.columnClient'), type: "text"},
                        {key: "subtotalMoney", label: this.$t('report.columnSubTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "amountPaid", label: this.$t('report.columnAmountPaid'), type: "money"},
                        {key: "amountOwed", label: this.$t('report.columnAmountOwed'), type: "money"}
                    ];
                }
                if (this.reportListType === "PAYMENTS_BY_DATE") {
                    return [
                        {key: "displayName", label: this.reportListFirstColumnLabel, type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "PAYMENTS_BY_CLIENT") {
                    return [
                        {key: "clientName", label: this.$t('report.columnClient'), type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "PAYMENTS_BY_TYPE") {
                    return [
                        {key: "type", label: this.$t('report.columnType'), type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "PAYMENTS_JOURNAL") {
                    return [
                        {key: "paymentDate", label: this.$t('report.columnPaymentDate'), type: "date"},
                        {key: "invoiceDate", label: this.$t('report.columnInvoiceDate'), type: "date"},
                        {key: "invoiceNo", label: this.$t('report.columnInvoiceNo'), type: "text"},
                        {key: "clientName", label: this.$t('report.columnClient'), type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "type", label: this.$t('report.columnType'), type: "text"}
                    ];
                }
                if (this.reportListType === "EXPENSE_BY_CATEGORY") {
                    return [
                        {key: "categoryName", label: this.$t('report.columnCategory'), type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "tipMoney", label: this.$t('expense.tip'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "EXPENSE_JOURNAL") {
                    return [
                        {key: "expenseDate", label: this.$t('report.columnExpenseDate'), type: "date"},
                        {key: "categoryName", label: this.$t('report.columnCategory'), type: "text"},
                        {key: "merchant", label: this.$t('report.columnMerchant'), type: "text"},
                        {key: "description", label: this.$t('report.columnDescription'), type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "tipMoney", label: this.$t('expense.tip'), type: "money"}
                    ];
                }
                if (this.reportListType === "PROFIT_LOSS") {
                    return [
                        {key: "sectionKey", label: this.$t('report.columnSection'), type: "translation"},
                        {key: "categoryName", translationKey: "categoryKey", label: this.$t('report.columnCategory'), type: "translationOrText"},
                        {key: "totalMoney", label: this.$t('report.columnAmount'), type: "money"}
                    ];
                }
                if (this.reportListType === "TAXES") {
                    return [
                        {key: "displayName", label: this.reportListFirstColumnLabel, type: "text"},
                        {key: "subtotalMoney", label: this.$t('report.columnPreTaxSales'), type: "money"},
                        {key: "taxName", label: this.$t('report.columnTaxLabel'), type: "text"},
                        {key: "taxRate", label: this.$t('report.columnTaxRate'), type: "taxRate"},
                        {key: "taxMoney", label: this.$t('report.columnTotalTax'), type: "money"},
                        {key: "totalMoney", label: this.$t('report.columnPostTaxTotal'), type: "money"}
                    ];
                }
                if (this.reportListType === "CUSTOMER_AGING") {
                    return [
                        {key: "clientName", label: this.$t('report.columnClient'), type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "currentDue", label: this.$t('report.columnCurrentDue'), type: "money"},
                        {key: "days1To30Overdue", label: this.$t('report.column1To30DaysOverdue'), type: "money"},
                        {key: "days31To60Overdue", label: this.$t('report.column31To60DaysOverdue'), type: "money"},
                        {key: "days61To90Overdue", label: this.$t('report.column61To90DaysOverdue'), type: "money"},
                        {key: "days91PlusOverdue", label: this.$t('report.column91PlusDaysOverdue'), type: "money"}
                    ];
                }
                return [];
            },
            reportListDonutChartList() {
                if (this.reportListType === "SALES_BY_CLIENT" || this.reportListType === "SALES_BY_ITEM" || this.reportListType === "PAYMENTS_BY_CLIENT") {
                    return this.reportListChartList.slice().sort((firstItem, secondItem) => {
                        return Number(secondItem.totalMoney) - Number(firstItem.totalMoney);
                    });
                }
                return this.reportListChartList;
            },
            reportListChartLegendList() {
                let totalMoney = 0;
                for (let i = 0; i < this.reportListDonutChartList.length; i++) {
                    totalMoney += Number(this.reportListDonutChartList[i].totalMoney);
                }
                return this.reportListDonutChartList.map((item, index) => {
                    let percent = 0;
                    if (totalMoney > 0) {
                        percent = Math.round(Number(item.totalMoney) / totalMoney * 1000) / 10;
                    }
                    return {
                        index: index,
                        name: item.name,
                        key: item.key,
                        totalMoney: item.totalMoney,
                        percent: percent,
                        color: this.reportListColorList[index % this.reportListColorList.length]
                    };
                });
            },
            reportListDonutSliceList() {
                let totalMoney = 0;
                for (let i = 0; i < this.reportListDonutChartList.length; i++) {
                    totalMoney += Number(this.reportListDonutChartList[i].totalMoney);
                }
                if (totalMoney === 0) {
                    return [];
                }
                let currentAngle = -90;
                return this.reportListDonutChartList.map((item, index) => {
                    let sliceAngle = Number(item.totalMoney) / totalMoney * 360;
                    let startAngle = currentAngle;
                    let endAngle = currentAngle + sliceAngle;
                    currentAngle = endAngle;
                    return {
                        index: index,
                        key: item.key,
                        name: item.name,
                        totalMoney: item.totalMoney,
                        color: this.reportListColorList[index % this.reportListColorList.length],
                        path: this.getDonutSlicePath(100, 100, 70, 42, startAngle, endAngle)
                    };
                });
            },
            filteredReportListDetailList() {
                let keyword = this.reportListSearchContent.trim().toLocaleLowerCase();
                if (keyword === "" || !this.showReportListSearch) {
                    return this.reportListDetailList;
                }
                let searchKeys = [];
                if (this.reportListType === "SALES_BY_CLIENT" || this.reportListType === "PAYMENTS_BY_CLIENT") {
                    searchKeys = ["clientName"];
                } else if (this.reportListType === "SALES_BY_ITEM") {
                    searchKeys = ["itemName"];
                } else if (this.reportListType === "SALES_JOURNAL") {
                    searchKeys = ["invoiceNo", "clientName"];
                } else if (this.reportListType === "PAYMENTS_JOURNAL") {
                    searchKeys = ["invoiceNo", "clientName", "type"];
                }
                return this.reportListDetailList.filter(row => {
                    return searchKeys.some(key => String(row[key] == null ? "" : row[key])
                        .toLocaleLowerCase().includes(keyword));
                });
            },
            sortedReportListDetailList() {
                if (this.reportListSortKey === "") {
                    return this.filteredReportListDetailList;
                }
                let column = this.reportListColumnList.find(item => item.key === this.reportListSortKey);
                if (column == null) {
                    return this.filteredReportListDetailList;
                }
                return this.filteredReportListDetailList.slice().sort((firstRow, secondRow) => {
                    let firstValue = this.getReportListSortValue(firstRow, column);
                    let secondValue = this.getReportListSortValue(secondRow, column);
                    if (column.type === "text") {
                        return this.reportListSortOrder === 1
                            ? firstValue.localeCompare(secondValue)
                            : secondValue.localeCompare(firstValue);
                    }
                    return this.reportListSortOrder === 1
                        ? firstValue - secondValue
                        : secondValue - firstValue;
                });
            },
            showReportListEmptyChart() {
                return this.reportListChartType === "BAR" || this.reportListChartType === "DONUT";
            },
            chartGridTemplateColumns() {
                return "repeat(" + this.displayPeriodList.length + ", minmax(0, 1fr))";
            },
            chartWidth() {
                if (this.selectedYear !== null || this.displayPeriodList.length <= 10) {
                    return "100%";
                }
                return (this.displayPeriodList.length * 10) + "%";
            },
            chartViewportWidth() {
                return "100%";
            },
            reportListChartGridTemplateColumns() {
                return "repeat(" + this.reportListChartList.length + ", minmax(0, 1fr))";
            },
            reportListChartWidth() {
                if (this.reportListTimePeriodType !== "ALL_TIME" || this.reportListChartList.length <= 10) {
                    return "100%";
                }
                return (this.reportListChartList.length * 10) + "%";
            },
            displayPeriodList() {
                return this.reportBarList;
            },
            displayPeriodTableList() {
                if (this.selectedYear === null || this.groupType !== "MONTH") {
                    return this.displayPeriodList;
                }
                let tableEndMonthValue = this.getSelectedFiscalTableEndMonthValue();
                if (tableEndMonthValue === null) {
                    return [];
                }
                return this.displayPeriodList.filter(period => {
                    return this.getMonthValue(period.periodYear, period.periodMonth) <= tableEndMonthValue;
                });
            },
            totalOutstandingBalance() {
                let totalMoney = 0;
                for (let i = 0; i < this.outstandingBalanceList.length; i++) {
                    totalMoney += Number(this.outstandingBalanceList[i].totalOutstandingBalance);
                }
                return totalMoney;
            },
            displayOutstandingBalanceList() {
                return this.outstandingBalanceList.slice().sort((firstClient, secondClient) => {
                    return Number(secondClient.totalOutstandingBalance) - Number(firstClient.totalOutstandingBalance);
                }).slice(0, 5);
            },
            clientPieSliceList() {
                let totalMoney = 0;
                for (let i = 0; i < this.clientSalesList.length; i++) {
                    totalMoney += Number(this.clientSalesList[i].totalMoney);
                }
                if (totalMoney === 0) {
                    return [];
                }
                let currentAngle = -90;
                let sliceList = [];
                for (let i = 0; i < this.clientSalesList.length; i++) {
                    let client = this.clientSalesList[i];
                    let sliceAngle = Number(client.totalMoney) / totalMoney * 360;
                    let startAngle = currentAngle;
                    let endAngle = currentAngle + sliceAngle;
                    sliceList.push({
                        innerUUID: client.innerUUID,
                        name: client.name,
                        totalMoney: client.totalMoney,
                        color: client.color,
                        path: this.getDonutSlicePath(100, 100, 70, 42, startAngle, endAngle)
                    });
                    currentAngle = endAngle;
                }
                return sliceList;
            },
            maxReportMoney() {
                let maxMoney = 0;
                for (let i = 0; i < this.displayPeriodList.length; i++) {
                    let totalMoney = parseFloat(this.displayPeriodList[i].totalMoney);
                    if (totalMoney > maxMoney) {
                        maxMoney = totalMoney;
                    }
                }
                return maxMoney;
            },
            maxReportListMoney() {
                let maxMoney = 0;
                for (let i = 0; i < this.reportListChartList.length; i++) {
                    let totalMoney = parseFloat(this.reportListChartList[i].totalMoney);
                    if (totalMoney > maxMoney) {
                        maxMoney = totalMoney;
                    }
                }
                return maxMoney;
            },
        },
        methods: {
            initializeReportDates() {
                this.selectedYear = this.getCurrentFiscalYearEnd();
                this.selectedReportListYear = this.selectedYear;
                this.reportListStartYear = this.selectedYear;
                this.reportListEndYear = this.selectedYear;
                let today = new Date();
                today.setHours(0, 0, 0, 0);
                let todayText = this.formatReportDateInput(today);
                this.reportListCustomStartDate = todayText;
                this.reportListCustomEndDate = todayText;
            },
            restoreListQueryState() {
                const queryState = readListQueryState('report');
                if (queryState == null) {
                    return;
                }
                this.activeReportTab = queryState.activeReportTab;
                this.selectedYear = queryState.selectedYear;
                this.dateIncrement = queryState.dateIncrement;
                this.selectedReportListYear = queryState.selectedReportListYear;
                this.reportListPeriodSelectionType = queryState.reportListPeriodSelectionType;
                this.selectedReportListPeriodIndex = queryState.selectedReportListPeriodIndex;
                this.reportListCustomStartDate = queryState.reportListCustomStartDate;
                this.reportListCustomEndDate = queryState.reportListCustomEndDate;
                this.reportListDateIncrement = queryState.reportListDateIncrement;
                this.reportListType = queryState.reportListType;
                this.reportListSortKey = queryState.reportListSortKey;
                this.reportListSortOrder = queryState.reportListSortOrder;
                this.reportListSearchContent = queryState.reportListSearchContent;
            },
            saveListQueryState() {
                writeListQueryState('report', {
                    activeReportTab: this.activeReportTab,
                    selectedYear: this.selectedYear,
                    dateIncrement: this.dateIncrement,
                    selectedReportListYear: this.selectedReportListYear,
                    reportListPeriodSelectionType: this.reportListPeriodSelectionType,
                    selectedReportListPeriodIndex: this.selectedReportListPeriodIndex,
                    reportListCustomStartDate: this.reportListCustomStartDate,
                    reportListCustomEndDate: this.reportListCustomEndDate,
                    reportListDateIncrement: this.reportListDateIncrement,
                    reportListType: this.reportListType,
                    reportListSortKey: this.reportListSortKey,
                    reportListSortOrder: this.reportListSortOrder,
                    reportListSearchContent: this.reportListSearchContent
                });
            },
            async loadReportContext() {
                this.loading = true;
                try {
                    const response = await this.$axios.post('query_setting_and_company', {params: {}});
                    if (response.data.errorCode !== 0) {
                        throw new Error('Company and setting query failed');
                    }
                    const selectedData = syncCompanySelection(response.data);
                    if (selectedData == null) {
                        throw new Error('Selected company data is missing');
                    }
                    this.companyUUID = selectedData.company.innerUUID;
                    this.userSetting = selectedData.setting;
                    this.initializeReportDates();
                    this.restoreListQueryState();
                    this.contextLoaded = true;
                } catch (error) {
                    this.loadError = this.$t('common.loadFailed');
                    return;
                } finally {
                    this.loading = false;
                }
                if (this.activeReportTab === 'reportList') {
                    this.queryReportListData();
                    return;
                }
                this.queryReportData();
                this.queryTotalOutstandingBalance();
            },
            formatMoney(value) {
                return xiaoshudianfenge(value, this.userSetting);
            },
            formatReportListMoney(value) {
                return this.formatMoney(value);
            },
            reportListEpoch(value) {
                return Number(value == null || value === "" ? 0 : value);
            },
            formatReportListDate(value) {
                let date = new Date(this.reportListEpoch(value) * 1000);
                let monthNameList = this.$t('report.monthNames').split(',');
                return monthNameList[date.getMonth()] + " " + date.getDate() + ", " + date.getFullYear();
            },
            formatReportListCell(row, column) {
                if (row === this.reportListTotalRow && column === this.reportListColumnList[0]) {
                    return this.$t('report.totalRowLabel');
                }
                if (row === this.reportListTotalRow && column.type !== "money" && column.type !== "number") {
                    return "";
                }
                if (column.type === "translation") {
                    return this.$t('report.' + row[column.key]);
                }
                if (column.type === "translationOrText") {
                    let translationKey = row[column.translationKey];
                    return translationKey == null || translationKey === ""
                        ? row[column.key]
                        : this.$t('report.' + translationKey);
                }
                let value = row[column.key];
                if (value === undefined || value === null || value === "") {
                    if (column.type === "money") {
                        return this.formatReportListMoney(0);
                    }
                    if (column.type === "number") {
                        return 0;
                    }
                    if (column.type === "date") {
                        return "";
                    }
                    return "";
                }
                if (column.type === "money") {
                    return this.formatReportListMoney(value);
                }
                if (column.type === "number") {
                    return value;
                }
                if (column.type === "taxRate") {
                    return storedTaxRateToPercentage(value) + "%";
                }
                if (column.type === "date") {
                    return this.formatReportListDate(value);
                }
                if (column.type === "text" || column.type === "translation" || column.type === "translationOrText") {
                    return value;
                }
                return String(value);
            },
            getReportListSortValue(row, column) {
                let value = row[column.key];
                if (column.type === "text") {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    return String(value);
                }
                if (column.type === "date") {
                    return this.reportListEpoch(value);
                }
                if (column.type === "money" || column.type === "number" || column.type === "taxRate") {
                    if (value === undefined || value === null || value === "") {
                        return 0;
                    }
                    return Number(value);
                }
                return value == null ? "" : String(value);
            },
            changeReportListSort(column) {
                if (this.reportListSortKey !== column.key) {
                    this.reportListSortKey = column.key;
                    this.reportListSortOrder = 2;
                    return;
                }
                if (this.reportListSortOrder === 2) {
                    this.reportListSortOrder = 1;
                    return;
                }
                this.reportListSortOrder = 2;
            },
            startLoading() {
                this.loadingCount++;
                this.loading = true;
            },
            finishLoading() {
                this.loadingCount--;
                if (this.loadingCount === 0) {
                    this.loading = false;
                }
                if (this.loadingCount < 0) {
                    this.loadingCount = 0;
                    this.loading = false;
                }
            },
            getBarHeight(value) {
                return this.getBarHeightByMax(value, this.maxReportMoney);
            },
            getReportListBarHeight(value) {
                return this.getBarHeightByMax(value, this.maxReportListMoney);
            },
            getBarHeightByMax(value, maxMoney) {
                if (parseFloat(value) <= 0 || maxMoney === 0) {
                    return "0px";
                }
                return Math.max(16, parseFloat(value) / maxMoney * 240) + "px";
            },
            getSaleTooltip(period) {
                return period.periodName + " | " + this.formatMoney(period.totalMoney) + " | " + period.invoiceCount + " " + this.$t('report.invoicesCountSuffix');
            },
            getReportListSaleTooltip(period) {
                return period.displayName + " | " + this.formatReportListMoney(period.totalMoney) + " | " + period.invoiceCount + " " + this.$t('report.invoicesCountSuffix');
            },
            getReportListChartLabel(period) {
                return period.displayName;
            },
            getClientSaleTooltip(client) {
                return client.name + " | " + this.formatMoney(client.totalMoney);
            },
            getPointOnCircle(centerX, centerY, radius, angle) {
                let angleInRadians = angle * Math.PI / 180;
                return {
                    x: centerX + radius * Math.cos(angleInRadians),
                    y: centerY + radius * Math.sin(angleInRadians)
                };
            },
            getDonutSlicePath(centerX, centerY, outerRadius, innerRadius, startAngle, endAngle) {
                if (endAngle - startAngle >= 360) {
                    endAngle = startAngle + 359.99;
                }
                let largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
                let outerStart = this.getPointOnCircle(centerX, centerY, outerRadius, startAngle);
                let outerEnd = this.getPointOnCircle(centerX, centerY, outerRadius, endAngle);
                let innerStart = this.getPointOnCircle(centerX, centerY, innerRadius, startAngle);
                let innerEnd = this.getPointOnCircle(centerX, centerY, innerRadius, endAngle);
                return [
                    "M", outerStart.x, outerStart.y,
                    "A", outerRadius, outerRadius, 0, largeArcFlag, 1, outerEnd.x, outerEnd.y,
                    "L", innerEnd.x, innerEnd.y,
                    "A", innerRadius, innerRadius, 0, largeArcFlag, 0, innerStart.x, innerStart.y,
                    "Z"
                ].join(" ");
            },
            showClientPieTooltip(client, event) {
                this.clientPieTooltip.content = this.getClientSaleTooltip(client);
                this.clientPieTooltip.visible = true;
                this.moveClientPieTooltip(event);
            },
            moveClientPieTooltip(event) {
                let chartWrap = event.currentTarget.closest(".donut-chart-wrap");
                if (chartWrap == null) {
                    return;
                }
                let rect = chartWrap.getBoundingClientRect();
                this.clientPieTooltip.left = event.clientX - rect.left + 12;
                this.clientPieTooltip.top = event.clientY - rect.top + 12;
            },
            hideClientPieTooltip() {
                this.clientPieTooltip.visible = false;
            },
            scrollSaleChart(direction) {
                let chartScroll = this.$refs.saleChartScroll;
                if (chartScroll == null) {
                    return;
                }
                chartScroll.scrollLeft += direction * chartScroll.clientWidth;
            },
            handleSaleChartWheel(event) {
                if (this.selectedYear !== null) {
                    return;
                }
                let chartScroll = this.$refs.saleChartScroll;
                if (chartScroll == null) {
                    return;
                }
                let scrollDistance = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
                chartScroll.scrollLeft += scrollDistance;
                event.preventDefault();
            },
            scrollReportListChart(direction) {
                let chartScroll = this.$refs.reportListSaleChartScroll;
                if (chartScroll == null) {
                    return;
                }
                chartScroll.scrollLeft += direction * chartScroll.clientWidth;
            },
            handleReportListChartWheel(event) {
                if (this.reportListTimePeriodType !== "ALL_TIME") {
                    return;
                }
                let chartScroll = this.$refs.reportListSaleChartScroll;
                if (chartScroll == null) {
                    return;
                }
                let scrollDistance = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
                chartScroll.scrollLeft += scrollDistance;
                event.preventDefault();
            },
            parseReportDateInput(value) {
                let parts = value.split("-");
                let year = parseInt(parts[0], 10);
                let month = parseInt(parts[1], 10);
                let day = parseInt(parts[2], 10);
                let date = new Date(year, month - 1, day);
                date.setHours(0, 0, 0, 0);
                return date;
            },
            formatReportDateInput(date) {
                return date.getFullYear() + "-"
                    + String(date.getMonth() + 1).padStart(2, "0") + "-"
                    + String(date.getDate()).padStart(2, "0");
            },
            formatReportDate(date) {
                return new Intl.DateTimeFormat(this.$i18n.locale, {
                    year: "numeric",
                    month: "short",
                    day: "numeric"
                }).format(date);
            },
            formatReportMonth(date) {
                return new Intl.DateTimeFormat(this.$i18n.locale, {month: "long"}).format(date);
            },
            addDays(date, dayCount) {
                let result = new Date(date.getFullYear(), date.getMonth(), date.getDate() + dayCount);
                result.setHours(0, 0, 0, 0);
                return result;
            },
            addMonthsClamped(date, monthCount) {
                let targetFirstDay = new Date(date.getFullYear(), date.getMonth() + monthCount, 1);
                let targetLastDay = new Date(targetFirstDay.getFullYear(), targetFirstDay.getMonth() + 1, 0).getDate();
                let result = new Date(targetFirstDay.getFullYear(), targetFirstDay.getMonth(),
                    Math.min(date.getDate(), targetLastDay));
                result.setHours(0, 0, 0, 0);
                return result;
            },
            toEpochSecond(date) {
                return Math.floor(date.getTime() / 1000);
            },
            getReportListSelectedDateRange() {
                if (this.reportListTimePeriodType === "ALL_TIME") {
                    return null;
                }
                if (this.reportListTimePeriodType === "CUSTOM") {
                    let customStartDate = this.parseReportDateInput(this.reportListCustomStartDate);
                    let customEndDate = this.parseReportDateInput(this.reportListCustomEndDate);
                    return {
                        startDate: customStartDate,
                        endDate: this.addDays(customEndDate, 1)
                    };
                }
                if (this.selectedReportListYear === null) {
                    return null;
                }
                let taxYearRange = this.getFiscalYearDateRange(this.selectedReportListYear);
                if (this.reportListTimePeriodType === "TAX_YEAR") {
                    return taxYearRange;
                }
                if (this.reportListTimePeriodType === "QUARTER") {
                    return {
                        startDate: this.addMonthsClamped(taxYearRange.startDate,
                            (this.selectedReportListPeriodIndex - 1) * 3),
                        endDate: this.addMonthsClamped(taxYearRange.startDate,
                            this.selectedReportListPeriodIndex * 3)
                    };
                }
                if (this.reportListTimePeriodType === "MONTH") {
                    return {
                        startDate: this.addMonthsClamped(taxYearRange.startDate,
                            this.selectedReportListPeriodIndex - 1),
                        endDate: this.addMonthsClamped(taxYearRange.startDate,
                            this.selectedReportListPeriodIndex)
                    };
                }
                return null;
            },
            buildReportListTimePeriodParams() {
                if (this.reportListTimePeriodType === "ALL_TIME") {
                    return {timePeriodType: "ALL_TIME"};
                }
                if (this.reportListTimePeriodType === "TAX_YEAR") {
                    return {
                        timePeriodType: "TAX_YEAR",
                        year: this.selectedReportListYear
                    };
                }
                if (this.reportListTimePeriodType === "QUARTER"
                    || this.reportListTimePeriodType === "MONTH") {
                    if (this.selectedReportListYear === null) {
                        return {timePeriodType: "ALL_TIME"};
                    }
                    return {
                        timePeriodType: this.reportListTimePeriodType,
                        year: this.selectedReportListYear,
                        periodIndex: this.selectedReportListPeriodIndex
                    };
                }
                if (this.reportListTimePeriodType === "CUSTOM") {
                    let range = this.getReportListSelectedDateRange();
                    return {
                        timePeriodType: "CUSTOM",
                        customStartTime: this.toEpochSecond(range.startDate),
                        customEndTime: this.toEpochSecond(range.endDate)
                    };
                }
                return {timePeriodType: "ALL_TIME"};
            },
            formatReportPeriodName(periodName) {
                if (this.reportListGroupType === "YEAR") {
                    if (!/^\d{4}$/.test(periodName)) {
                        return String(periodName);
                    }
                    return periodName;
                }
                if (this.reportListGroupType === "QUARTER") {
                    let quarterMatch = /^(\d{4})-Q([1-4])$/.exec(periodName);
                    if (quarterMatch == null) {
                        return String(periodName);
                    }
                    return "Q" + quarterMatch[2] + " " + quarterMatch[1];
                }
                if (this.reportListGroupType === "MONTH") {
                    let monthMatch = /^(\d{4})-(\d{2})$/.exec(periodName);
                    if (monthMatch == null) {
                        return String(periodName);
                    }
                    let monthDate = this.parseReportDateInput(periodName + "-01");
                    return this.formatReportMonth(monthDate) + " " + monthDate.getFullYear();
                }
                if (this.reportListGroupType === "DAY") {
                    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(periodName))) {
                        return String(periodName);
                    }
                    return this.formatReportDate(this.parseReportDateInput(periodName));
                }
                if (this.reportListGroupType === "RANGE") {
                    let rangeParts = String(periodName).split("/");
                    if (rangeParts.length !== 2) {
                        return String(periodName);
                    }
                    return this.formatReportDate(this.parseReportDateInput(rangeParts[0]))
                        + " - " + this.formatReportDate(this.parseReportDateInput(rangeParts[1]));
                }
                return String(periodName);
            },
            localizeReportPeriodList(itemList) {
                return (Array.isArray(itemList) ? itemList : []).filter(item => item != null).map(item => {
                    if (item.periodName == null || item.periodName === "") {
                        return item;
                    }
                    return Object.assign({}, item, {
                        displayName: this.formatReportPeriodName(item.periodName)
                    });
                });
            },
            getTaxYearStart() {
                let taxYearStart = this.userSetting == null ? 0 : this.reportListEpoch(this.userSetting.taxYearStart);
                let taxYearStartDate = new Date(taxYearStart * 1000);
                return {
                    month: taxYearStartDate.getMonth() + 1,
                    day: taxYearStartDate.getDate()
                };
            },
            getCurrentFiscalYearEnd() {
                let taxYearStart = this.getTaxYearStart();
                let currentDate = new Date();
                if (taxYearStart.month === 1 && taxYearStart.day === 1) {
                    return currentDate.getFullYear();
                }
                let currentYearStartDate = new Date(currentDate.getFullYear(), taxYearStart.month - 1, taxYearStart.day);
                if (currentDate.getTime() >= currentYearStartDate.getTime()) {
                    return currentDate.getFullYear() + 1;
                }
                return currentDate.getFullYear();
            },
            getFiscalYearDateRange(fiscalYearEnd) {
                let taxYearStart = this.getTaxYearStart();
                let startYear = fiscalYearEnd - 1;
                let endYear = fiscalYearEnd;
                if (taxYearStart.month === 1 && taxYearStart.day === 1) {
                    startYear = fiscalYearEnd;
                    endYear = fiscalYearEnd + 1;
                }
                return {
                    startDate: new Date(startYear, taxYearStart.month - 1, taxYearStart.day),
                    endDate: new Date(endYear, taxYearStart.month - 1, taxYearStart.day)
                };
            },
            getMonthValue(year, month) {
                return year * 12 + month;
            },
            getMonthValueFromDate(date) {
                return this.getMonthValue(date.getFullYear(), date.getMonth() + 1);
            },
            getPreviousDate(date) {
                return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
            },
            getPeriodName(year, month) {
                return year + "-" + String(month).padStart(2, "0");
            },
            getSelectedFiscalTableEndMonthValue() {
                let fiscalRange = this.getFiscalYearDateRange(this.selectedYear);
                let startMonthValue = this.getMonthValueFromDate(fiscalRange.startDate);
                let fiscalEndMonthValue = this.getMonthValueFromDate(this.getPreviousDate(fiscalRange.endDate));
                let currentMonthValue = this.getMonthValueFromDate(new Date());
                if (currentMonthValue < startMonthValue) {
                    return null;
                }
                if (currentMonthValue > fiscalEndMonthValue) {
                    return fiscalEndMonthValue;
                }
                return currentMonthValue;
            },
            changeDateIncrement(dateIncrement) {
                this.dateIncrement = dateIncrement;
                this.queryReportSaleStatistics();
            },
            changeReportTab(reportTab) {
                this.activeReportTab = reportTab;
                if (reportTab === "reportList") {
                    this.queryReportListData();
                } else {
                    this.reportStickyScrollbarVisible = false;
                }
            },
            changeReportListPeriodSelectionType() {
                this.selectedReportListPeriodIndex = 1;
                this.reportListDateIncrement = "month";
                this.reportListSortKey = "";
                this.reportListSortOrder = "";
                this.queryReportListData();
            },
            changeReportListCustomDate() {
                this.queryReportListData();
            },
            getInvoiceListTimeQuery(year) {
                if (year === null) {
                    return {
                        timePeriodType: "ALL_TIME"
                    };
                }
                return {
                    timePeriodType: "TAX_YEAR",
                    year: year
                };
            },
            getReportListInvoiceTimeQuery() {
                if (this.reportListTimePeriodType === "ALL_TIME") {
                    return this.getInvoiceListTimeQuery(null);
                }
                if (this.reportListTimePeriodType === "TAX_YEAR") {
                    return this.getInvoiceListTimeQuery(this.selectedReportListYear);
                }
                let range = this.getReportListSelectedDateRange();
                return {
                    timePeriodType: "ALL_TIME",
                    startInvoiceDate: this.toEpochSecond(range.startDate),
                    endInvoiceDate: this.toEpochSecond(range.endDate)
                };
            },
            getFiscalYearEndByPeriod(period) {
                if (period.fiscalQuarter !== null && period.periodYear != null) {
                    return period.periodYear;
                }
                let periodDate = new Date(period.periodYear, period.periodMonth - 1, 1);
                let currentFiscalYear = this.getCurrentFiscalYearEnd();
                for (let year = currentFiscalYear + 5; year >= currentFiscalYear - 10; year--) {
                    let fiscalRange = this.getFiscalYearDateRange(year);
                    if (periodDate.getTime() >= fiscalRange.startDate.getTime() && periodDate.getTime() < fiscalRange.endDate.getTime()) {
                        return year;
                    }
                }
                return Number.isInteger(Number(period.periodYear))
                    ? Number(period.periodYear) : currentFiscalYear;
            },
            goInvoiceListBySalePeriod(period) {
                let fiscalYear = this.selectedYear === null
                    ? this.getFiscalYearEndByPeriod(period) : this.selectedYear;
                let periodRange = this.getSalePeriodDateRange(period, fiscalYear);
                let query = this.getInvoiceListTimeQuery(null);
                query.startInvoiceDate = this.toEpochSecond(periodRange.startDate);
                query.endInvoiceDate = this.toEpochSecond(periodRange.endDate);
                this.$router.push({
                    path: "/invoice",
                    query: query
                });
            },
            getSalePeriodDateRange(period, fiscalYear) {
                let fiscalRange = this.getFiscalYearDateRange(fiscalYear);
                if (period.fiscalQuarter != null) {
                    return {
                        startDate: this.addMonthsClamped(fiscalRange.startDate, (period.fiscalQuarter - 1) * 3),
                        endDate: this.addMonthsClamped(fiscalRange.startDate, period.fiscalQuarter * 3)
                    };
                }
                let monthStartDate = new Date(period.periodYear, period.periodMonth - 1, 1);
                let monthEndDate = new Date(period.periodYear, period.periodMonth, 1);
                if (this.selectedYear === null) {
                    return {
                        startDate: monthStartDate,
                        endDate: monthEndDate
                    };
                }
                return {
                    startDate: monthStartDate.getTime() < fiscalRange.startDate.getTime()
                        ? fiscalRange.startDate : monthStartDate,
                    endDate: monthEndDate.getTime() > fiscalRange.endDate.getTime()
                        ? fiscalRange.endDate : monthEndDate
                };
            },
            goInvoiceListByClient(client) {
                if (client.innerUUID === '') {
                    return;
                }
                let query = this.getInvoiceListTimeQuery(this.selectedYear);
                query.numberOrClient = client.name;
                query.clientUUID = client.innerUUID;
                this.$router.push({
                    path: "/invoice",
                    query: query
                });
            },
            goInvoiceListByReportClient(client) {
                if (this.reportListType !== "SALES_BY_CLIENT") {
                    return;
                }
                let query = this.getReportListInvoiceTimeQuery();
                query.numberOrClient = client.name;
                query.clientUUID = client.key;
                this.$router.push({
                    path: "/invoice",
                    query: query
                });
            },
            goInvoiceListByReportClientRow(row) {
                if (this.reportListType !== "SALES_BY_CLIENT") {
                    return;
                }
                let query = this.getReportListInvoiceTimeQuery();
                query.numberOrClient = row.clientName;
                query.clientUUID = row.clientUUID;
                this.$router.push({
                    path: "/invoice",
                    query: query
                });
            },
            changeReportListType() {
                this.reportListDateIncrement = "month";
                this.reportListSortKey = "";
                this.reportListSortOrder = "";
                this.reportListSearchContent = "";
                this.queryReportListData();
            },
            applyReportListYearBounds(reportData) {
                const startYear = Number(reportData == null ? NaN : reportData.startYear);
                const endYear = Number(reportData == null ? NaN : reportData.endYear);
                if (!Number.isInteger(startYear) || !Number.isInteger(endYear) || startYear > endYear) {
                    return false;
                }
                this.reportListStartYear = startYear;
                this.reportListEndYear = endYear;
                let selectedYearIsOutsideBounds = this.selectedReportListYear < startYear
                    || this.selectedReportListYear > endYear;
                if (!selectedYearIsOutsideBounds) {
                    return false;
                }
                this.selectedReportListYear = endYear;
                if (this.reportListTimePeriodType === "TAX_YEAR"
                    || this.reportListTimePeriodType === "QUARTER"
                    || this.reportListTimePeriodType === "MONTH") {
                    this.reportListPeriodSelectionType = "ALL_TIME";
                    this.selectedReportListPeriodIndex = 1;
                    return true;
                }
                return false;
            },
            queryReportData() {
                this.queryReportSaleStatistics();
                this.queryAnnualSalesByClient();
            },
            queryReportSaleStatistics() {
                this.startLoading();
                let requestSequence = ++this.reportSaleRequestSequence;
                this.reportSaleLoadError = '';
                this.reportBarList = [];
                this.reportSummary = {totalInvoiceCount: 0, totalMoney: 0};
                let params = {
                    type: 0,
                    companyUUID: this.companyUUID,
                    timeZone: getBrowserTimeZone(),
                    groupType: this.groupType
                };
                if (this.selectedYear !== null) {
                    params.year = this.selectedYear;
                }
                this.$axios.post('query_invoice_sale_statistics', {
                    params: params
                }).then(response => {
                    if (requestSequence === this.reportSaleRequestSequence) {
                        if (response.data.errorCode === 0) {
                            this.reportBarList = this.buildSalePeriodList(
                                Array.isArray(response.data.dataList) ? response.data.dataList : []);
                            this.reportSummary = this.buildSaleSummary(this.reportBarList);
                        } else {
                            this.reportSaleLoadError = this.$t('common.loadFailed');
                        }
                    }
                    this.finishLoading();
                }).catch(() => {
                    this.finishLoading();
                    if (requestSequence === this.reportSaleRequestSequence) {
                        this.reportSaleLoadError = this.$t('common.loadFailed');
                    }
                })
            },
            queryReportListData() {
                this.startLoading();
                let requestSequence = ++this.reportListRequestSequence;
                this.reportListLoadError = '';
                this.reportListLoaded = false;
                this.reportStickyScrollbarVisible = false;
                this.reportListSummary = {
                    reportType: this.reportListType,
                    currencyName: "",
                    summaryCount: 0,
                    summaryCountName: "",
                    summaryTotalMoney: 0
                };
                this.reportListChartType = "NONE";
                this.reportListChartList = [];
                this.reportListDetailList = [];
                this.reportListTotalRow = {};
                let params = {
                    reportType: this.reportListType,
                    companyUUID: this.companyUUID,
                    timeZone: getBrowserTimeZone()
                };
                if (this.showReportListTimePeriodFilter) {
                    Object.assign(params, this.buildReportListTimePeriodParams());
                }
                if (this.isInvoiceReportType(this.reportListType)) {
                    params.type = 0;
                }
                if (this.reportListRequiresGroupType) {
                    params.groupType = this.reportListGroupType;
                }
                if (this.reportListRequiresGroupType && this.reportListTimePeriodType === "ALL_TIME") {
                    params.nowTime = getBrowserStartOfTodayEpochSecond();
                }
                if (this.reportListType === "CUSTOMER_AGING") {
                    params.nowTime = getBrowserStartOfTodayEpochSecond();
                }
                this.$axios.post('query_report_data', {
                    params: params
                }).then(response => {
                    if (requestSequence !== this.reportListRequestSequence) {
                        this.finishLoading();
                        return;
                    }
                    if (response.data.errorCode === 0) {
                        let reportData = response.data.objectData || {};
                        if (this.showReportListTimePeriodFilter && this.applyReportListYearBounds(reportData)) {
                            this.finishLoading();
                            this.queryReportListData();
                            return;
                        }
                        this.reportListSummary = {
                            reportType: reportData.reportType,
                            currencyName: reportData.currencyName,
                            summaryCount: reportData.summaryCount,
                            summaryCountName: reportData.summaryCountName,
                            summaryTotalMoney: reportData.summaryTotalMoney
                        };
                        this.reportListChartType = reportData.chartType;
                        this.reportListChartList = this.localizeReportPeriodList(reportData.chartList);
                        this.reportListDetailList = this.localizeReportPeriodList(reportData.dataList);
                        this.reportListTotalRow = reportData.totalRow || {};
                        this.reportListLoaded = true;
                        this.refreshReportStickyScrollbar();
                    } else {
                        this.reportListLoadError = this.$t('common.loadFailed');
                    }
                    this.finishLoading();
                }).catch(() => {
                    this.finishLoading();
                    if (requestSequence === this.reportListRequestSequence) {
                        this.reportListLoadError = this.$t('common.loadFailed');
                    }
                })
            },
            refreshReportStickyScrollbar() {
                this.$nextTick(() => {
                    const tableScroll = this.$refs.reportTableScroll;
                    const stickyScrollbar = this.$refs.reportStickyScrollbar;
                    if (tableScroll == null || stickyScrollbar == null) {
                        this.reportStickyScrollbarVisible = false;
                        return;
                    }
                    const tableBounds = tableScroll.getBoundingClientRect();
                    this.reportStickyScrollbarLeft = Math.max(0, tableBounds.left);
                    this.reportStickyScrollbarViewportWidth = Math.max(
                        0, Math.min(tableBounds.width, window.innerWidth - this.reportStickyScrollbarLeft)
                    );
                    this.reportStickyScrollbarContentWidth = tableScroll.scrollWidth;
                    this.reportStickyScrollbarVisible = window.innerWidth < 1440
                        && tableScroll.scrollWidth > tableScroll.clientWidth + 1;
                    const trackWidth = Math.max(0, this.reportStickyScrollbarViewportWidth - 8);
                    this.reportStickyScrollbarThumbWidth = Math.min(
                        trackWidth,
                        Math.max(56, trackWidth * tableScroll.clientWidth / tableScroll.scrollWidth)
                    );
                    this.syncReportStickyScrollbarFromTable();
                });
            },
            syncReportStickyScrollbarFromTable() {
                const tableScroll = this.$refs.reportTableScroll;
                if (tableScroll == null) {
                    return;
                }
                this.reportStickyScrollbarScrollLeft = Math.round(tableScroll.scrollLeft);
                const tableMaxScroll = tableScroll.scrollWidth - tableScroll.clientWidth;
                const thumbMaxTravel = this.reportStickyScrollbarViewportWidth - 8
                    - this.reportStickyScrollbarThumbWidth;
                this.reportStickyScrollbarThumbLeft = tableMaxScroll > 0
                    ? thumbMaxTravel * tableScroll.scrollLeft / tableMaxScroll
                    : 0;
            },
            setReportScrollFromStickyThumb(thumbLeft) {
                const tableScroll = this.$refs.reportTableScroll;
                if (tableScroll == null) {
                    return;
                }
                const thumbMaxTravel = this.reportStickyScrollbarViewportWidth - 8
                    - this.reportStickyScrollbarThumbWidth;
                const normalizedThumbLeft = Math.max(0, Math.min(thumbMaxTravel, thumbLeft));
                const tableMaxScroll = tableScroll.scrollWidth - tableScroll.clientWidth;
                tableScroll.scrollLeft = thumbMaxTravel > 0
                    ? tableMaxScroll * normalizedThumbLeft / thumbMaxTravel
                    : 0;
                this.syncReportStickyScrollbarFromTable();
            },
            moveReportTableFromStickyTrack(event) {
                const trackBounds = this.$refs.reportStickyScrollbarTrack.getBoundingClientRect();
                this.setReportScrollFromStickyThumb(
                    event.clientX - trackBounds.left - this.reportStickyScrollbarThumbWidth / 2
                );
            },
            startReportStickyScrollbarDrag(event) {
                this.reportStickyScrollbarDragStartX = event.clientX;
                this.reportStickyScrollbarDragStartLeft = this.reportStickyScrollbarThumbLeft;
                document.addEventListener('mousemove', this.dragReportStickyScrollbar);
                document.addEventListener('mouseup', this.stopReportStickyScrollbarDrag);
            },
            dragReportStickyScrollbar(event) {
                this.setReportScrollFromStickyThumb(
                    this.reportStickyScrollbarDragStartLeft
                    + event.clientX - this.reportStickyScrollbarDragStartX
                );
            },
            stopReportStickyScrollbarDrag() {
                document.removeEventListener('mousemove', this.dragReportStickyScrollbar);
                document.removeEventListener('mouseup', this.stopReportStickyScrollbarDrag);
            },
            scrollReportTable(direction) {
                const tableScroll = this.$refs.reportTableScroll;
                if (tableScroll == null) {
                    return;
                }
                tableScroll.scrollBy({
                    left: direction * Math.max(240, tableScroll.clientWidth * 0.7),
                    behavior: 'smooth'
                });
            },
            isInvoiceReportType(reportType) {
                return reportType === "SALES_BY_DATE"
                    || reportType === "SALES_BY_CLIENT"
                    || reportType === "SALES_BY_ITEM"
                    || reportType === "SALES_JOURNAL"
                    || reportType === "PROFIT_LOSS"
                    || reportType === "TAXES";
            },
            queryAnnualSalesByClient() {
                this.startLoading();
                let requestSequence = ++this.clientSalesRequestSequence;
                this.clientSalesLoadError = '';
                this.clientSalesList = [];
                let params = {
                    companyUUID: this.companyUUID,
                    timeZone: getBrowserTimeZone()
                };
                if (this.selectedYear !== null) {
                    params.year = this.selectedYear;
                }
                this.$axios.post('query_annual_sales_by_client', {
                    params: params
                }).then(response => {
                    if (requestSequence === this.clientSalesRequestSequence) {
                        if (response.data.errorCode === 0) {
                            this.clientSalesList = this.buildClientSalesList(response.data.dataList);
                        } else {
                            this.clientSalesLoadError = this.$t('common.loadFailed');
                        }
                    }
                    this.finishLoading();
                }).catch(() => {
                    this.finishLoading();
                    if (requestSequence === this.clientSalesRequestSequence) {
                        this.clientSalesLoadError = this.$t('common.loadFailed');
                    }
                })
            },
            queryTotalOutstandingBalance() {
                this.startLoading();
                let requestSequence = ++this.outstandingRequestSequence;
                this.outstandingLoadError = '';
                this.outstandingBalanceList = [];
                this.$axios.post('query_total_outstanding_balance', {
                    params: {
                        companyUUID: this.companyUUID
                    }
                }).then(response => {
                    if (requestSequence === this.outstandingRequestSequence) {
                        if (response.data.errorCode === 0) {
                            this.outstandingBalanceList = response.data.dataList;
                        } else {
                            this.outstandingLoadError = this.$t('common.loadFailed');
                        }
                    }
                    this.finishLoading();
                }).catch(() => {
                    this.finishLoading();
                    if (requestSequence === this.outstandingRequestSequence) {
                        this.outstandingLoadError = this.$t('common.loadFailed');
                    }
                })
            },
            buildSalePeriodList(dataList) {
                if (this.selectedYear === null && this.groupType === "MONTH") {
                    return this.buildAllYearMonthPeriodList(dataList);
                }
                if (this.selectedYear === null && this.groupType === "QUARTER") {
                    return this.buildAllYearQuarterPeriodList(dataList);
                }
                if (this.selectedYear !== null && this.groupType === "MONTH") {
                    return this.buildSelectedFiscalMonthPeriodList(dataList);
                }
                if (this.selectedYear !== null && this.groupType === "QUARTER") {
                    let quarterMap = {};
                    for (let i = 0; i < dataList.length; i++) {
                        quarterMap[dataList[i].fiscalQuarter] = dataList[i];
                    }
                    let quarterList = [];
                    for (let quarter = 4; quarter >= 1; quarter--) {
                        if (quarterMap[quarter] == null) {
                            quarterList.push({
                                periodKey: this.selectedYear + "-Q" + quarter,
                                periodName: this.selectedYear + "-Q" + quarter,
                                title: this.selectedYear + "-Q" + quarter,
                                periodYear: this.selectedYear,
                                periodMonth: null,
                                fiscalQuarter: quarter,
                                invoiceCount: 0,
                                totalMoney: 0
                            });
                        } else {
                            quarterList.push({
                                periodKey: this.selectedYear + "-Q" + quarter,
                                periodName: this.selectedYear + "-Q" + quarter,
                                title: this.selectedYear + "-Q" + quarter,
                                periodYear: this.selectedYear,
                                periodMonth: quarterMap[quarter].periodMonth,
                                fiscalQuarter: quarter,
                                invoiceCount: quarterMap[quarter].invoiceCount,
                                totalMoney: quarterMap[quarter].totalMoney
                            });
                        }
                    }
                    return quarterList;
                }
                return dataList.map(item => {
                    return this.buildSalePeriod(item);
                });
            },
            buildAllYearMonthPeriodList(dataList) {
                if (dataList.length === 0) {
                    return [];
                }
                let periodMap = {};
                let minPeriodValue = null;
                let maxPeriodValue = null;
                for (let i = 0; i < dataList.length; i++) {
                    let item = dataList[i];
                    let periodValue = this.getMonthValue(item.periodYear, item.periodMonth);
                    periodMap[periodValue] = item;
                    if (minPeriodValue === null || periodValue < minPeriodValue) {
                        minPeriodValue = periodValue;
                    }
                    if (maxPeriodValue === null || periodValue > maxPeriodValue) {
                        maxPeriodValue = periodValue;
                    }
                }
                let periodList = [];
                for (let periodValue = maxPeriodValue; periodValue >= minPeriodValue; periodValue--) {
                    let periodYear = Math.floor((periodValue - 1) / 12);
                    let periodMonth = periodValue - periodYear * 12;
                    let periodName = this.getPeriodName(periodYear, periodMonth);
                    if (periodMap[periodValue] == null) {
                        periodList.push({
                            periodKey: periodName,
                            periodName: periodName,
                            title: periodName,
                            periodYear: periodYear,
                            periodMonth: periodMonth,
                            fiscalQuarter: null,
                            invoiceCount: 0,
                            totalMoney: 0
                        });
                    } else {
                        periodList.push(this.buildSalePeriod(periodMap[periodValue]));
                    }
                }
                return periodList;
            },
            buildSelectedFiscalMonthPeriodList(dataList) {
                let fiscalRange = this.getFiscalYearDateRange(this.selectedYear);
                let startMonthValue = this.getMonthValueFromDate(fiscalRange.startDate);
                let endMonthValue = this.getMonthValueFromDate(this.getPreviousDate(fiscalRange.endDate));
                let periodMap = {};
                for (let i = 0; i < dataList.length; i++) {
                    periodMap[dataList[i].periodName] = dataList[i];
                }
                let periodList = [];
                for (let periodValue = endMonthValue; periodValue >= startMonthValue; periodValue--) {
                    let periodYear = Math.floor((periodValue - 1) / 12);
                    let periodMonth = periodValue - periodYear * 12;
                    let periodName = this.getPeriodName(periodYear, periodMonth);
                    if (periodMap[periodName] == null) {
                        periodList.push({
                            periodKey: periodName,
                            periodName: periodName,
                            title: periodName,
                            periodYear: periodYear,
                            periodMonth: periodMonth,
                            fiscalQuarter: null,
                            invoiceCount: 0,
                            totalMoney: 0
                        });
                    } else {
                        periodList.push(this.buildSalePeriod(periodMap[periodName]));
                    }
                }
                return periodList;
            },
            buildAllYearQuarterPeriodList(dataList) {
                if (dataList.length === 0) {
                    return [];
                }
                let periodMap = {};
                let minPeriodValue = null;
                let maxPeriodValue = null;
                for (let i = 0; i < dataList.length; i++) {
                    let item = dataList[i];
                    let periodValue = item.periodYear * 4 + item.fiscalQuarter;
                    periodMap[periodValue] = item;
                    if (minPeriodValue === null || periodValue < minPeriodValue) {
                        minPeriodValue = periodValue;
                    }
                    if (maxPeriodValue === null || periodValue > maxPeriodValue) {
                        maxPeriodValue = periodValue;
                    }
                }
                let periodList = [];
                for (let periodValue = maxPeriodValue; periodValue >= minPeriodValue; periodValue--) {
                    let periodYear = Math.floor((periodValue - 1) / 4);
                    let fiscalQuarter = periodValue - periodYear * 4;
                    let periodName = periodYear + "-Q" + fiscalQuarter;
                    if (periodMap[periodValue] == null) {
                        periodList.push({
                            periodKey: periodName,
                            periodName: periodName,
                            title: periodName,
                            periodYear: periodYear,
                            periodMonth: null,
                            fiscalQuarter: fiscalQuarter,
                            invoiceCount: 0,
                            totalMoney: 0
                        });
                    } else {
                        periodList.push(this.buildSalePeriod(periodMap[periodValue]));
                    }
                }
                return periodList;
            },
            buildSalePeriod(item) {
                return {
                    periodKey: item.periodName,
                    periodName: item.periodName,
                    title: item.periodName,
                    periodYear: item.periodYear,
                    periodMonth: item.periodMonth,
                    fiscalQuarter: item.fiscalQuarter,
                    invoiceCount: item.invoiceCount,
                    totalMoney: item.totalMoney
                };
            },
            buildClientSalesList(dataList) {
                let sortedDataList = dataList.slice().sort((firstClient, secondClient) => {
                    return Number(secondClient.totalMoney) - Number(firstClient.totalMoney);
                });
                let totalMoney = 0;
                for (let i = 0; i < sortedDataList.length; i++) {
                    totalMoney += Number(sortedDataList[i].totalMoney);
                }
                let visibleDataList = sortedDataList.slice(0, 5);
                if (sortedDataList.length > visibleDataList.length) {
                    let otherMoney = 0;
                    for (let i = visibleDataList.length; i < sortedDataList.length; i++) {
                        otherMoney += Number(sortedDataList[i].totalMoney);
                    }
                    visibleDataList.push({
                        innerUUID: '',
                        name: this.$t('report.otherClients'),
                        totalMoney: otherMoney
                    });
                }
                return visibleDataList.map((item, index) => {
                    let percent = 0;
                    if (totalMoney > 0) {
                        percent = Math.round(Number(item.totalMoney) / totalMoney * 1000) / 10;
                    }
                    return {
                        innerUUID: item.innerUUID,
                        name: item.name,
                        totalMoney: item.totalMoney,
                        percent: percent,
                        color: this.clientColorList[index % this.clientColorList.length]
                    };
                });
            },
            buildSaleSummary(periodList) {
                let totalInvoiceCount = 0;
                let totalMoney = 0;
                for (let i = 0; i < periodList.length; i++) {
                    totalInvoiceCount += Number(periodList[i].invoiceCount);
                    totalMoney += Number(periodList[i].totalMoney);
                }
                return {
                    totalInvoiceCount: totalInvoiceCount,
                    totalMoney: totalMoney
                };
            },
            escapeCsvValue(value) {
                let text = String(value);
                if (text.indexOf('"') >= 0 || text.indexOf(",") >= 0
                    || text.indexOf("\n") >= 0 || text.indexOf("\r") >= 0) {
                    return '"' + text.replace(/"/g, '""') + '"';
                }
                return text;
            },
            getReportListExportFileName() {
                let reportTypeOption = this.reportTypeOptions.find(option => option.value === this.reportListType);
                if (reportTypeOption == null) {
                    return "Report.csv";
                }
                let fileName = this.reportListType === "CUSTOMER_AGING"
                    ? reportTypeOption.label
                    : reportTypeOption.label + " - " + this.reportListSelectedPeriodTitle;
                return fileName.replace(/[\\/:*?"<>|]/g, "-") + ".csv";
            },
            exportReportListCsv() {
                let header = this.reportListColumnList.map(column => {
                    return column.label;
                });
                let rowList = [header];
                for (let i = 0; i < this.sortedReportListDetailList.length; i++) {
                    let row = this.sortedReportListDetailList[i];
                    rowList.push(this.reportListColumnList.map(column => {
                        return this.formatReportListCell(row, column);
                    }));
                }
                if (this.showReportListTotalRow) {
                    rowList.push(this.reportListColumnList.map(column => {
                        return this.formatReportListCell(this.reportListTotalRow, column);
                    }));
                }
                let csvContent = rowList.map(row => {
                    return row.map(value => {
                        return this.escapeCsvValue(value);
                    }).join(",");
                }).join("\n");
                let blob = new Blob(["\uFEFF", csvContent], {type: "text/csv;charset=utf-8;"});
                let url = URL.createObjectURL(blob);
                let link = document.createElement("a");
                link.href = url;
                link.download = this.getReportListExportFileName();
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        },
        mounted() {
            this.loadReportContext();
            window.addEventListener('resize', this.refreshReportStickyScrollbar);
        },
        beforeRouteLeave(to, from, next) {
            this.saveListQueryState();
            next();
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.refreshReportStickyScrollbar);
            this.stopReportStickyScrollbarDrag();
        }
    }
</script>

<style scoped>
    /deep/ .el-header {
        background-color: #ffffff;
        font-size: 25px;
        font-weight: 700;
        height: 100px !important;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 30px;
        border-bottom: 1px solid #E4E7ED;
    }

    .report-main {
        padding: 0;
        background: #F6F7F9;
    }

    .report-tabs {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding: 26px 30px 0;
        background: #ffffff;
        border-bottom: 1px solid #E4E7ED;
    }

    .report-tab-list {
        display: flex;
        gap: 26px;
    }

    .report-tab {
        padding: 0 0 14px;
        color: #5F6368;
        font-size: 16px;
        font-weight: 600;
        background: transparent;
        border: none;
        border-bottom: 4px solid transparent;
        cursor: pointer;
    }

    .report-tab.active {
        color: #111111;
        border-bottom-color: #FF4B1F;
    }

    .export-button {
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
        width: 150px;
        height: 44px;
        padding: 0 22px;
        color: #ffffff;
        font-size: 16px;
        font-weight: 700;
        background: #3445FF;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        transform: translateY(-14px);
    }

    .export-button:disabled {
        color: #6B7280;
        background: #E5E7EB;
        cursor: not-allowed;
    }

    .report-overview-section {
        padding-bottom: 24px;
    }

    .report-overview-grid {
        display: grid;
        grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
        gap: 64px;
    }

    .report-panel {
        min-width: 0;
    }

    .report-section {
        padding: 32px 30px;
        background: #ffffff;
        border-bottom: 12px solid #F0F2F5;
    }

    .report-section h2 {
        margin: 0 0 10px;
        color: #111111;
        font-size: 22px;
        font-weight: 700;
    }

    .report-section-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
    }

    .invoice-count {
        color: #111111;
        font-size: 15px;
        font-weight: 600;
    }

    .total-sales {
        margin-top: 6px;
        color: #000000;
        font-size: 36px;
        font-weight: 700;
    }

    .year-select {
        width: 150px;
    }

    .report-tabs .year-select {
        transform: translateY(-14px);
    }

    .report-mode-tabs {
        display: flex;
        justify-content: center;
        margin-top: 20px;
    }

    .mode-button {
        padding: 8px 14px;
        color: #5F6368;
        font-size: 15px;
        background: #F5F6F8;
        border: 1px solid #F0F1F3;
        cursor: pointer;
    }

    .mode-button.active {
        color: #ffffff;
        background: #333333;
        border-color: #333333;
    }

    .bar-chart-wrap {
        margin-top: 20px;
    }

    .bar-chart-actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-bottom: 8px;
    }

    .bar-chart-scroll {
        max-width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
    }

    .bar-chart {
        display: grid;
        gap: 3px;
        align-items: end;
        min-height: 310px;
        padding-bottom: 2px;
        position: relative;
    }

    .bar-chart:before {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 38px;
        height: 1px;
        background: #DADDE3;
    }

    .bar-column {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        min-width: 0;
        position: relative;
        z-index: 1;
    }

    .bar-track {
        height: 260px;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        width: 100%;
        cursor: pointer;
    }

    .bar-value {
        width: 72px;
        background: #217BD3;
        transition: height 0.18s ease;
    }

    .bar-label {
        height: 36px;
        padding-top: 10px;
        box-sizing: border-box;
        color: #737373;
        font-size: var(--responsive-font-14);
    }

    .period-list {
        margin-top: 34px;
        max-height: 500px;
        overflow-y: auto;
    }

    .empty-report {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 220px;
        margin-top: 20px;
        color: #737373;
        font-size: 15px;
        border: 1px dashed #DADDE3;
    }

    .period-row,
    .client-sales-row,
    .outstanding-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-height: 50px;
        border-bottom: 1px solid #E0E2E6;
        color: #111111;
        font-size: 16px;
        font-weight: 600;
    }

    .period-row,
    .report-list-donut-row-item,
    .report-list-clickable-row {
        cursor: pointer;
    }

    .client-chart-row {
        display: grid;
        grid-template-columns: 200px minmax(260px, 1fr);
        gap: 24px;
        align-items: center;
    }

    .donut-chart {
        width: 200px;
        height: 200px;
    }

    .donut-chart-wrap {
        width: 200px;
        height: 200px;
        position: relative;
    }

    .donut-empty {
        fill: none;
        stroke-width: 56;
        stroke: #E5E7EB;
    }

    .donut-slice {
        cursor: pointer;
    }

    .client-pie-tooltip {
        position: absolute;
        z-index: 10;
        padding: 8px 10px;
        color: #ffffff;
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
        background: rgba(51, 51, 51, 0.94);
        border-radius: 4px;
        pointer-events: none;
    }

    .client-sales-list {
        min-width: 0;
        overflow: visible;
    }

    .client-sales-row,
    .outstanding-row {
        cursor: pointer;
    }

    .client-color {
        width: 4px;
        height: 36px;
        margin-right: 14px;
    }

    .client-name {
        flex: 1;
    }

    .outstanding-total {
        margin-top: 14px;
        color: #000000;
        font-size: 36px;
        font-weight: 700;
    }

    .outstanding-list {
        margin-top: 20px;
        max-height: 500px;
        overflow-y: auto;
    }

    .report-list-section {
        padding: 28px 30px 20px;
        background: #ffffff;
        border-bottom: 12px solid #F0F2F5;
    }

    .report-list-filters {
        display: flex;
        align-items: flex-start;
        gap: 28px;
    }

    .report-list-filter {
        width: 180px;
    }

    .report-list-filter-compact {
        width: 100px;
    }

    .report-list-date-filter {
        width: 150px;
    }

    .report-list-filter span {
        display: block;
        margin-bottom: 4px;
        color: #333333;
        font-size: 13px;
        font-weight: 500;
    }

    .report-list-select {
        width: 100%;
    }

    /deep/ .report-list-select .el-input__inner {
        height: 32px;
        padding: 0 28px 0 0;
        color: #111111;
        font-size: 16px;
        font-weight: 700;
        background: transparent;
        border: none;
        border-bottom: 2px solid #D4D7DC;
        border-radius: 0;
    }

    /deep/ .report-list-date-filter .el-input__inner {
        padding-left: 0;
    }

    /deep/ .report-list-date-filter .el-input__prefix {
        display: none;
    }

    /deep/ .report-list-select .el-input__suffix {
        right: 0;
    }

    .report-list-summary {
        margin-top: 28px;
    }

    .report-list-chart-wrap {
        margin-top: 42px;
    }

    .report-list-donut-row {
        display: grid;
        grid-template-columns: 300px minmax(360px, 1fr);
        gap: 72px;
        align-items: center;
        margin-top: 54px;
        min-height: 300px;
    }

    .report-list-donut-wrap {
        width: 260px;
        height: 260px;
    }

    .report-list-donut {
        display: block;
        width: 260px;
        height: 260px;
    }

    .report-list-donut-legend {
        min-width: 0;
        max-height: 500px;
        overflow-y: auto;
    }

    .report-list-donut-row-item {
        display: grid;
        grid-template-columns: 4px minmax(180px, 1fr) minmax(140px, auto);
        gap: 14px;
        align-items: center;
        min-height: 50px;
        border-bottom: 1px solid #E0E2E6;
        color: #111111;
        font-size: 16px;
    }

    .report-detail-section {
        padding: 28px 30px 48px;
        background: #ffffff;
    }

    .report-list-table-search {
        width: min(360px, 100%);
        margin-bottom: 20px;
    }

    .report-detail-table-wrap {
        overflow-x: auto;
    }

    .report-sticky-scrollbar {
        position: fixed;
        bottom: 0;
        z-index: 40;
        height: 20px;
        padding: 3px 4px;
        box-sizing: border-box;
        background: #f8fbff;
        border: 1px solid #d7e3f2;
        border-bottom: 0;
        box-shadow: 0 -4px 14px rgba(18, 38, 77, 0.1);
    }

    .report-sticky-scrollbar:focus-visible {
        border-color: #8eb8ee;
        outline: 2px solid rgba(47, 119, 241, 0.28);
        outline-offset: -2px;
    }

    .report-sticky-scrollbar-track {
        position: relative;
        width: 100%;
        height: 12px;
        background: #e8eef7;
        border-radius: 999px;
        cursor: pointer;
    }

    .report-sticky-scrollbar-thumb {
        position: absolute;
        top: 1px;
        left: 0;
        height: 10px;
        background: #7f9ec9;
        border-radius: 999px;
        cursor: grab;
        transition: background 150ms ease;
        will-change: transform;
    }

    .report-sticky-scrollbar-thumb:hover {
        background: #5f82b2;
    }

    .report-sticky-scrollbar-thumb:active {
        cursor: grabbing;
    }

    .report-detail-table {
        width: 100%;
        min-width: 1100px;
        border-collapse: collapse;
        color: #111111;
        table-layout: fixed;
    }

    .report-detail-table th,
    .report-detail-table td {
        width: 120px;
        padding: 20px 18px;
        text-align: right;
        border-bottom: 2px solid #DADDE3;
        font-size: 16px;
        line-height: 1.4;
        overflow-wrap: anywhere;
        word-break: break-word;
        white-space: normal;
    }

    .report-detail-table .list-sort-header {
        max-width: 100%;
        white-space: normal;
    }

    .report-detail-table .list-sort-icon {
        flex: 0 0 auto;
    }

    .report-detail-table th {
        color: #666D76;
        font-weight: 500;
        border-bottom-color: #666D76;
        cursor: pointer;
    }

    .report-detail-table th:first-child,
    .report-detail-table td:first-child {
        width: 220px;
        text-align: left;
    }

    .report-detail-table td {
        font-weight: 500;
    }

    .report-detail-table td:first-child {
        font-weight: 700;
    }

    .report-list-clickable-row {
        cursor: pointer;
    }

    .report-detail-table th:last-child,
    .report-detail-table td:last-child {
        width: 48px;
    }

    .sales-by-client-table th:first-child,
    .sales-by-client-table td:first-child,
    .sales-by-item-table th:first-child,
    .sales-by-item-table td:first-child,
    .sales-journal-table th:nth-child(3),
    .sales-journal-table td:nth-child(3),
    .customer-aging-table th:first-child,
    .customer-aging-table td:first-child {
        white-space: normal;
        overflow-wrap: anywhere;
        word-break: break-word;
        line-height: 1.4;
    }

    .sales-by-date-year-table th:first-child,
    .sales-by-date-year-table td:first-child {
        width: 90px;
    }

    .taxes-year-table .taxes-year-col {
        width: 44px;
    }

    .taxes-year-table .taxes-value-col {
        width: calc((100% - 44px) / 5);
    }

    .taxes-year-table th:first-child,
    .taxes-year-table td:first-child {
        width: 44px;
        padding-right: 4px;
        padding-left: 4px;
    }

    .taxes-table th:last-child,
    .taxes-table td:last-child {
        width: 160px;
    }

    .sales-journal-table th:nth-child(3),
    .sales-journal-table td:nth-child(3) {
        width: 180px;
    }

    .report-compact-table {
        min-width: 0;
    }

    .report-compact-table th,
    .report-compact-table td {
        padding-right: 12px;
        padding-left: 12px;
    }

    .report-compact-table th:first-child,
    .report-compact-table td:first-child,
    .profit-loss-table th:nth-child(2),
    .profit-loss-table td:nth-child(2) {
        white-space: normal;
        overflow-wrap: anywhere;
        word-break: break-word;
        line-height: 1.4;
    }

    .report-compact-table th:last-child,
    .report-compact-table td:last-child {
        width: 72px;
    }

    .profit-loss-table th:last-child,
    .profit-loss-table td:last-child {
        width: 30%;
    }

    .sales-journal-table th:first-child,
    .sales-journal-table td:first-child {
        width: 110px;
    }

    .sales-journal-table th:nth-child(2),
    .sales-journal-table td:nth-child(2) {
        width: 110px;
    }

    .sales-journal-table th:nth-child(5),
    .sales-journal-table td:nth-child(5),
    .sales-journal-table th:nth-child(6),
    .sales-journal-table td:nth-child(6),
    .sales-journal-table th:nth-child(7),
    .sales-journal-table td:nth-child(7) {
        width: 110px;
    }

    .sales-journal-table th:nth-child(8),
    .sales-journal-table td:nth-child(8),
    .sales-journal-table th:nth-child(9),
    .sales-journal-table td:nth-child(9) {
        width: 150px;
    }

    .payments-journal-table th:nth-child(1),
    .payments-journal-table td:nth-child(1),
    .payments-journal-table th:nth-child(2),
    .payments-journal-table td:nth-child(2),
    .payments-journal-table th:nth-child(3),
    .payments-journal-table td:nth-child(3) {
        width: 110px;
    }

    .payments-journal-table th:nth-child(4),
    .payments-journal-table td:nth-child(4) {
        width: 300px;
    }

    .payments-journal-table th:nth-child(5),
    .payments-journal-table td:nth-child(5) {
        width: 220px;
    }

    .payments-journal-table th:nth-child(6),
    .payments-journal-table td:nth-child(6) {
        width: 160px;
    }

    .customer-aging-table {
        min-width: 1480px;
    }

    .customer-aging-table .customer-aging-client-col {
        width: 160px;
    }

    .customer-aging-table th:first-child,
    .customer-aging-table td:first-child {
        width: 160px;
    }

    .customer-aging-table th:last-child,
    .customer-aging-table td:last-child {
        width: 220px;
    }

    @media (max-width: 1440px) {
        .report-detail-table th,
        .report-detail-table td {
            font-size: 15px;
        }
    }

    @media (max-width: 1100px) {
        .report-overview-grid {
            grid-template-columns: 1fr;
        }

        .report-list-filters {
            flex-wrap: wrap;
        }
    }

    @media (max-width: 720px) {
        /deep/ .el-header {
            height: 86px !important;
            padding: 28px 72px 20px 20px;
        }

        .report-tabs {
            flex-wrap: wrap;
            gap: 14px;
            padding: 18px 16px 0;
        }

        .report-tab-list {
            width: 100%;
            gap: 20px;
        }

        .export-button {
            width: 124px;
            height: 40px;
            padding: 0 16px;
            transform: none;
        }

        .client-chart-row,
        .report-list-donut-row {
            grid-template-columns: minmax(0, 1fr);
            gap: 22px;
            justify-items: center;
        }

        .client-sales-list,
        .report-list-donut-legend {
            width: 100%;
        }

        .report-list-donut-row {
            margin-top: 28px;
        }

        .report-list-donut-row-item {
            grid-template-columns: 4px minmax(100px, 1fr) minmax(90px, auto);
            gap: 10px;
            font-size: var(--responsive-font-14);
        }

        .report-detail-section {
            padding: 22px 16px 36px;
        }
    }
</style>
